import React from 'react';
import PropTypes from 'prop-types';
import {
    TeamMatchupLayerWrapper,
    ScoreContainer,
    ScoreWrapper,
    RowWrapper,
    RecordWrapper
} from './TeamMatchupLayer.style';
import teamsArray from 'bdata-sns-shared/game/components/teamsArray';
import GameStateTypes from 'bdata-sns-shared/game/types/GameStateTypes';
import getScoresData from 'bdata-sns-shared/utils/getScoresData';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import breakpoints from 'bdata-sns-shared/mini/utils/miniBreakpoints';
import getTranslation from 'bdata-sns-shared/utils/getTranslation';
import MiniSBGameModes from 'bdata-sns-shared/mini/types/MiniSBGameModes';

// styling for record differs from style guide so generating record here instead of baked in component
export const generateRecord = (teams, team, isLoading) => {
    const { leagueRecord: { wins = 0, losses = 0 } = {} } = teams[team];
    const formattedRecord = `${wins} - ${losses}`;
    return isLoading
        ? <Skeleton width={ 32 } height={ 8 } margin={ 0 } />
        : formattedRecord;
};

// Team matchup + scores / records based on game state
const TeamMatchupLayer = ({
    mode,
    teams,
    isMedium,
    isLoading,
    dictionary,
    linescore,
    gameState,
    customLogoSize = 18,
    customNameFontSize = 14,
    showTeamRecordsInPreview = true,
    showSplitSquad = false,
    teamLogoType,
    showTeamRecordsInFinal = true,
    isGameOver,
    isActiveGame
}) => {
    const {
        homeScore,
        awayScore
    } = getScoresData(linescore, teams);

    const isPreview = gameState === GameStateTypes.PREVIEW;
    const isFullyFinal = gameState === GameStateTypes.FINAL && !isGameOver;
    const showScoreState = !isActiveGame && !isPreview;
    const showRecord = !isActiveGame && ((isPreview && showTeamRecordsInPreview) || (isFullyFinal && showTeamRecordsInFinal));

    const teamMatchupArr = teamsArray(
        teams,
        {
            isMobile: isMedium,
            alwaysLeftAligned: true,
            isLoading,
            hideRecord: true,
            customLogoSize,
            customNameFontSize,
            DesktopBreakpoint: breakpoints.medium,
            MobileBreakpoint: breakpoints.medium,
            splitSquadLabel: showSplitSquad ? getTranslation(dictionary, 'split_squad_abbrev', 'SS') : '',
            teamLogoType
        }
    );
    const liveWithFavoriteTeam = (gameState === 'Live') && (teams?.away?.isFavorite || teams?.home?.isFavorite);

    const TeamMatchups = teamMatchupArr.map((team, i) => {
        return (
            <RowWrapper key={ i }>
                { team }
                { showRecord
                    ? <RecordWrapper
                        isFullyFinal={ isFullyFinal }
                        scoreHidden={ typeof awayScore === 'undefined'}
                    >
                        { generateRecord(teams, i === 0 ? 'away' : 'home', isLoading) }
                    </RecordWrapper>
                    : null
                }
                { showScoreState && !isLoading
                    ? <ScoreContainer>
                        <ScoreWrapper className={`${liveWithFavoriteTeam ? 'fave' : ''}`}>{ i === 0 ? awayScore : homeScore }</ScoreWrapper>
                    </ScoreContainer>
                    : null
                }
            </RowWrapper>
        );
    });

    return (
        <TeamMatchupLayerWrapper>
            { TeamMatchups }
        </TeamMatchupLayerWrapper>
    );
};

TeamMatchupLayer.propTypes = {
    mode: PropTypes.oneOf(Object.values(MiniSBGameModes)),
    teams: PropTypes.object,
    isMedium: PropTypes.bool,
    isLoading: PropTypes.bool,
    dictionary: PropTypes.object,
    linescore: PropTypes.object,
    gameState: PropTypes.oneOf(Object.values(GameStateTypes)),
    customLogoSize: PropTypes.number,
    showSplitSquad: PropTypes.bool,
    customNameFontSize: PropTypes.number,
    showTeamRecordsInPreview: PropTypes.bool,
    showTeamRecordsInFinal: PropTypes.bool,
    isGameOver: PropTypes.bool,
    isActiveGame: PropTypes.bool,
    teamLogoType: PropTypes.string
};

export default TeamMatchupLayer;
